/**
 * Environment variable configuration and validation for the PWA.
 * Similar to the backend's env.ts, this provides type-safe access to environment variables
 * and validates that all required variables are present at build time.
 */

/**
 * Build-time constant injected via vite.config.ts define option
 * Format: YYYY.MM.DD.HHMM (e.g., "2024.12.04.1030")
 */
declare const __APP_VERSION__: string

/**
 * Base environment variables required for all environments
 */
interface BaseEnvironmentVariables {
    // Runtime environment
    ENVIRONMENT: 'development' | 'production' | 'staging'

    // Build version (injected at build time)
    APP_VERSION: string

    // API configuration
    API_URL: string
    APP_URL: string

    // Cloudflare R2 configuration
    R2_ASSETS_URL: string

    // Firebase configuration (required for all environments)
    FIREBASE_API_KEY: string
    FIREBASE_AUTH_DOMAIN: string
    FIREBASE_PROJECT_ID: string
    FIREBASE_STORAGE_BUCKET: string
    FIREBASE_MESSAGING_SENDER_ID: string
    FIREBASE_APP_ID: string
    FIREBASE_MEASUREMENT_ID: string

    // Stripe configuration
    // STRIPE_PUBLISHABLE_KEY: string

    // Sentry configuration (optional in all environments)
    SENTRY_DSN?: string
}

/**
 * Production-specific environment variables
 */
interface ProductionEnvironmentVariables extends BaseEnvironmentVariables {
    ENVIRONMENT: 'production'

    // PostHog configuration (required only in production)
    POSTHOG_KEY: string
    POSTHOG_HOST: string
}

/**
 * Development/Staging environment variables (PostHog optional)
 */
interface NonProductionEnvironmentVariables extends BaseEnvironmentVariables {
    ENVIRONMENT: 'development' | 'staging'

    // PostHog configuration (optional in non-production)
    POSTHOG_KEY?: string
    POSTHOG_HOST?: string
}

/**
 * Union type representing all possible environment configurations
 */
export type EnvironmentVariables =
    | ProductionEnvironmentVariables
    | NonProductionEnvironmentVariables

/**
 * Validates environment variables and returns a typed configuration object.
 * Throws an error if required variables are missing or invalid.
 */
function validateEnv(): EnvironmentVariables {
    const rawEnv = import.meta.env

    // Validate ENVIRONMENT
    const environment =
        rawEnv.VITE_ENVIRONMENT as EnvironmentVariables['ENVIRONMENT']
    const validEnvironments = ['development', 'production', 'staging'] as const

    if (!environment || !validEnvironments.includes(environment)) {
        throw new Error(
            `Invalid or missing VITE_ENVIRONMENT: "${environment}". Must be one of: ${validEnvironments.join(', ')}`
        )
    }

    // Base required variables for all environments
    const baseRequiredVars = [
        'VITE_API_URL',
        'VITE_APP_URL',
        'VITE_R2_ASSETS_URL',
        'VITE_FIREBASE_API_KEY',
        'VITE_FIREBASE_AUTH_DOMAIN',
        'VITE_FIREBASE_PROJECT_ID',
        'VITE_FIREBASE_STORAGE_BUCKET',
        'VITE_FIREBASE_MESSAGING_SENDER_ID',
        'VITE_FIREBASE_APP_ID',
        'VITE_FIREBASE_MEASUREMENT_ID',
        // 'VITE_STRIPE_PUBLISHABLE_KEY',
    ] as const

    // Check base required variables
    const missingBaseVars = baseRequiredVars.filter((key) => !rawEnv[key])

    if (missingBaseVars.length > 0) {
        throw new Error(
            `Missing required environment variables: ${missingBaseVars.join(', ')}\n` +
                `Available variables: ${Object.keys(rawEnv)
                    .filter((k) => k.startsWith('VITE_'))
                    .join(', ')}`
        )
    }

    // Production-specific validation
    if (environment === 'production') {
        const productionRequiredVars = [
            'VITE_PUBLIC_POSTHOG_KEY',
            'VITE_PUBLIC_POSTHOG_HOST',
        ] as const

        const missingProductionVars = productionRequiredVars.filter(
            (key) => !rawEnv[key]
        )

        if (missingProductionVars.length > 0) {
            throw new Error(
                `Missing required PRODUCTION environment variables: ${missingProductionVars.join(', ')}`
            )
        }

        return {
            ENVIRONMENT: environment,
            APP_VERSION: __APP_VERSION__,
            API_URL: rawEnv.VITE_API_URL!,
            APP_URL: rawEnv.VITE_APP_URL!,
            R2_ASSETS_URL: rawEnv.VITE_R2_ASSETS_URL!,
            FIREBASE_API_KEY: rawEnv.VITE_FIREBASE_API_KEY!,
            FIREBASE_AUTH_DOMAIN: rawEnv.VITE_FIREBASE_AUTH_DOMAIN!,
            FIREBASE_PROJECT_ID: rawEnv.VITE_FIREBASE_PROJECT_ID!,
            FIREBASE_STORAGE_BUCKET: rawEnv.VITE_FIREBASE_STORAGE_BUCKET!,
            FIREBASE_MESSAGING_SENDER_ID:
                rawEnv.VITE_FIREBASE_MESSAGING_SENDER_ID!,
            FIREBASE_APP_ID: rawEnv.VITE_FIREBASE_APP_ID!,
            FIREBASE_MEASUREMENT_ID: rawEnv.VITE_FIREBASE_MEASUREMENT_ID!,
            // STRIPE_PUBLISHABLE_KEY: rawEnv.VITE_STRIPE_PUBLISHABLE_KEY!,
            POSTHOG_KEY: rawEnv.VITE_PUBLIC_POSTHOG_KEY!,
            POSTHOG_HOST: rawEnv.VITE_PUBLIC_POSTHOG_HOST!,
            SENTRY_DSN: rawEnv.VITE_SENTRY_DSN,
        }
    }

    // Development/Staging (PostHog optional)
    return {
        ENVIRONMENT: environment,
        APP_VERSION: __APP_VERSION__,
        API_URL: rawEnv.VITE_API_URL!,
        APP_URL: rawEnv.VITE_APP_URL!,
        R2_ASSETS_URL: rawEnv.VITE_R2_ASSETS_URL!,
        FIREBASE_API_KEY: rawEnv.VITE_FIREBASE_API_KEY!,
        FIREBASE_AUTH_DOMAIN: rawEnv.VITE_FIREBASE_AUTH_DOMAIN!,
        FIREBASE_PROJECT_ID: rawEnv.VITE_FIREBASE_PROJECT_ID!,
        FIREBASE_STORAGE_BUCKET: rawEnv.VITE_FIREBASE_STORAGE_BUCKET!,
        FIREBASE_MESSAGING_SENDER_ID: rawEnv.VITE_FIREBASE_MESSAGING_SENDER_ID!,
        FIREBASE_APP_ID: rawEnv.VITE_FIREBASE_APP_ID!,
        FIREBASE_MEASUREMENT_ID: rawEnv.VITE_FIREBASE_MEASUREMENT_ID!,
        // STRIPE_PUBLISHABLE_KEY: rawEnv.VITE_STRIPE_PUBLISHABLE_KEY!,
        POSTHOG_KEY: rawEnv.VITE_PUBLIC_POSTHOG_KEY,
        POSTHOG_HOST: rawEnv.VITE_PUBLIC_POSTHOG_HOST,
        SENTRY_DSN: rawEnv.VITE_SENTRY_DSN,
    }
}

/**
 * Exported configuration object.
 * Use this to access environment variables throughout the application.
 *
 * @example
 * import { env } from '@/config/env';
 * console.log(env.API_URL);
 * console.log(env.APP_VERSION); // e.g., "2024.12.04.1030"
 *
 * // PostHog in production only
 * if (env.ENVIRONMENT === 'production') {
 *   console.log(env.POSTHOG_KEY);
 * }
 */
export const env = validateEnv()
